import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { noticesApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Bell,
  ArrowLeft,
  AlertTriangle,
  Wrench,
  Calendar,
  Info,
  Pin,
  ChevronRight,
} from 'lucide-react';

export default function NoticesPage() {
  const { user, activeBuilding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [notices, setNotices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [errorMessage, setErrorMessage] = useState(null);

  const hoaId = user?.hoa_id || activeBuilding?.hoa_id || null;

  useEffect(() => {
    if (hoaId) {
      fetchNotices();
    } else {
      setLoading(false);
    }
  }, [filter, hoaId]);

  const fetchNotices = async () => {
    try {
      setLoading(true);
      setErrorMessage(null);
      const category = filter === 'all' ? null : filter;
      const data = await noticesApi.list(hoaId, category);

      // Sort: pinned first, then by date
      const sorted = (Array.isArray(data) ? data : []).sort((a, b) => {
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setNotices(sorted);
    } catch (error) {
      console.error('Error fetching notices:', error);
      setNotices([]);
      const msg = error?.message || 'Could not load notices — please refresh';
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'urgent': return <AlertTriangle className="w-5 h-5" />;
      case 'maintenance': return <Wrench className="w-5 h-5" />;
      case 'event': return <Calendar className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700',
      maintenance: 'bg-amber-100 text-amber-700',
      event: 'bg-sky-100 text-sky-700',
      general: 'bg-slate-100 text-slate-700',
    };
    return colors[category] || colors.general;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m ago`;
    }
    if (diff < 86400000) {
      const hours = Math.floor(diff / 3600000);
      return `${hours}h ago`;
    }
    if (diff < 604800000) {
      const days = Math.floor(diff / 86400000);
      return `${days}d ago`;
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-slate-200/50 px-4 py-3">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <Link to="/dashboard">
            <Button variant="ghost" size="icon" data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <h1 className="text-lg font-semibold font-manrope text-slate-900">Notices</h1>
        </div>
      </header>

      <main className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Filter */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {['all', 'urgent', 'maintenance', 'event', 'general'].map((category) => (
            <Button
              key={category}
              variant={filter === category ? 'default' : 'outline'}
              size="sm"
              className={`rounded-full whitespace-nowrap capitalize ${filter === category ? 'bg-slate-900' : ''}`}
              onClick={() => setFilter(category)}
              data-testid={`filter-${category}`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Notices List */}
        <div className="space-y-3">
          {!hoaId ? (
            <Card data-testid="no-building-card">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">No building linked</h3>
                <p className="text-slate-500 text-sm mb-4">
                  Your account isn't connected to a building yet. Join a building to see its notices.
                </p>
                <Link to="/find-building">
                  <Button className="bg-sky-500 hover:bg-sky-600" data-testid="join-building-btn">Find a building</Button>
                </Link>
              </CardContent>
            </Card>
          ) : loading ? (
            <>
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
              <Skeleton className="h-28 rounded-xl" />
            </>
          ) : notices.length > 0 ? (
            notices.map((notice, index) => (
              <Card 
                key={notice.notice_id} 
                className={`overflow-hidden animate-slideUp cursor-pointer hover:shadow-md transition-shadow ${
                  notice.category === 'urgent' ? 'border-red-200 bg-red-50/30' : ''
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`notice-${notice.notice_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${getCategoryColor(notice.category)}`}>
                      {getCategoryIcon(notice.category)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {notice.is_pinned && (
                          <Pin className="w-3 h-3 text-slate-400" />
                        )}
                        <Badge variant="secondary" className={`text-xs ${getCategoryColor(notice.category)}`}>
                          {notice.category}
                        </Badge>
                      </div>
                      <p className="font-medium text-slate-900">{notice.title}</p>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{notice.description}</p>
                      <p className="text-xs text-slate-400 mt-2">{formatDate(notice.created_at)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 flex-shrink-0" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card data-testid="notices-empty-card">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Bell className="w-8 h-8 text-slate-400" />
                </div>
                <h3 className="font-semibold text-slate-900 mb-1">
                  {errorMessage ? 'Couldn\'t load notices' : 'No notices'}
                </h3>
                <p className="text-slate-500 text-sm">
                  {errorMessage
                    ? errorMessage
                    : filter === 'all'
                      ? "No community notices at the moment"
                      : `No ${filter} notices found`}
                </p>
                {errorMessage && (
                  <Button size="sm" variant="outline" className="mt-4" onClick={fetchNotices} data-testid="retry-notices-btn">
                    Try again
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
